import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import { useEffect, useState } from 'react';
import type { EditorContext, Identity } from '../../types';
import { SuggestionList, createSuggestionRenderer } from '../suggestion';

/**
 * `@<identity-guid>` — Azure DevOps wiki mention. ADO stores the identity id,
 * not the display name, so the chip resolves the name on demand and caches it.
 */

export interface UserMentionOptions {
  ctx: EditorContext | null;
}

const cache = new Map<string, Identity | null>();

function resolve(ctx: EditorContext | null, id: string): Promise<Identity | null> {
  if (!ctx) return Promise.resolve(null);
  if (cache.has(id)) return Promise.resolve(cache.get(id)!);
  return ctx.api
    .getIdentity(id)
    .catch(() => null)
    .then(i => {
      cache.set(id, i);
      return i;
    });
}

export function primeIdentityCache(items: Identity[]) {
  for (const i of items) cache.set(i.id, i);
}

function MentionChip({ node, extension, selected }: NodeViewProps) {
  const ctx = (extension.options as UserMentionOptions).ctx;
  const id = String(node.attrs.id);
  const initial = node.attrs.label ? ({ id, displayName: node.attrs.label } as Identity) : cache.get(id);
  const [identity, setIdentity] = useState<Identity | null | undefined>(initial);

  useEffect(() => {
    if (initial) return;
    let live = true;
    resolve(ctx, id).then(i => live && setIdentity(i));
    return () => {
      live = false;
    };
  }, [ctx, id, initial]);

  return (
    <NodeViewWrapper as="span" className={`user-mention${selected ? ' is-selected' : ''}`} data-type="mention" data-id={id} title={identity?.mail ?? identity?.account ?? id} contentEditable={false}>
      @{identity === undefined ? '…' : identity === null ? id.slice(0, 8) : identity.displayName}
    </NodeViewWrapper>
  );
}

function IdentitySuggestionItem(item: Identity) {
  return (
    <>
      <span className="suggestion-title">{item.displayName}</span>
      <span className="suggestion-meta">{item.mail ?? item.account}</span>
    </>
  );
}

const GUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

export const UserMention = Node.create<UserMentionOptions>({
  name: 'userMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { ctx: null };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: el => el.getAttribute('data-id'),
        renderHTML: attrs => ({ 'data-id': attrs.id }),
      },
      // Display name is a convenience for rendering only; it is never serialized to markdown.
      label: { default: null, rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="mention"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes({ 'data-type': 'mention', class: 'user-mention' }, HTMLAttributes), `@${node.attrs.label ?? node.attrs.id}`];
  },

  renderText({ node }) {
    return `@<${node.attrs.id}>`;
  },

  // ---- markdown ----
  markdownTokenName: 'userMention',
  markdownTokenizer: {
    name: 'userMention',
    level: 'inline',
    start(src) {
      const i = src.indexOf('@<');
      return i;
    },
    tokenize(src) {
      const m = new RegExp(`^@<(${GUID})>`).exec(src);
      if (!m) return undefined;
      return { type: 'userMention', raw: m[0], id: m[1] };
    },
  },
  parseMarkdown(token, h) {
    return h.createNode('userMention', { id: token.id });
  },
  renderMarkdown(node) {
    return `@<${node.attrs?.id}>`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionChip, { as: 'span' });
  },

  addProseMirrorPlugins() {
    const ctx = this.options.ctx;
    return [
      Suggestion<Identity>({
        editor: this.editor,
        pluginKey: new PluginKey('userMentionSuggestion'),
        char: '@',
        allowSpaces: true,
        items: async ({ query }) => {
          if (!ctx || query.trim().length < 2) return [];
          try {
            const items = await ctx.api.searchIdentities(query);
            primeIdentityCache(items);
            return items;
          } catch {
            return [];
          }
        },
        command: ({ editor, range, props }) => {
          editor
            .chain()
            .focus()
            .insertContentAt(range, [{ type: 'userMention', attrs: { id: props.id, label: props.displayName } }, { type: 'text', text: ' ' }])
            .run();
        },
        render: createSuggestionRenderer<Identity>(SuggestionList, { renderItem: IdentitySuggestionItem, emptyText: 'Type at least two letters of a name' }),
      }),
    ];
  },
});
